import { useEffect, useState } from 'react'
import { api, ApiError, can, type Dashboard, type ReportRows, type MemberRows } from '../lib/api'

/** The dashboard and the historical reports.
 *
 *  PLAN.html §24: "Two different tools, deliberately kept apart." So they are
 *  two sections here, not one screen with a date picker bolted on — the
 *  dashboard answers a glance and takes no configuration; the report answers a
 *  question somebody asked and takes a range.
 *
 *  MEDIAN LEADS, THE MEAN SITS BEHIND IT
 *
 *  §24: "One call where a responder was stuck behind a train drags an average
 *  badly and makes a good month look poor." So the median is the big number
 *  and the mean is small text beside it.
 *
 *  EVERY FIGURE CARRIES ITS DENOMINATOR
 *
 *  "Median 8 minutes" over twelve calls of which three were timed is the
 *  middle of three, and looks identical to the honest version. So each tile
 *  says what it was computed from.
 *
 *  THE CHART IS INLINE SVG
 *
 *  Not a charting library. This project has added exactly one npm dependency
 *  (leaflet, for a real map) and a bar chart of 24 numbers does not earn a
 *  second one. */

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function minutes(v: number | null): string {
  if (v === null || v === undefined) return '—'
  if (v < 1) return '<1 min'
  if (v < 90) return `${v} min`
  return `${(v / 60).toFixed(1)} h`
}

function Bars({ data, labels, title }: {
  data: number[]; labels: string[]; title: string
}) {
  const max = Math.max(1, ...data)
  const w = 100 / data.length
  return (
    <div className="chart">
      <h4>{title}</h4>
      <svg viewBox="0 0 100 34" preserveAspectRatio="none" className="chartsvg">
        {data.map((v, i) => (
          <rect
            key={i}
            x={i * w + w * 0.15} width={w * 0.7}
            y={30 - (v / max) * 28} height={(v / max) * 28 || 0.3}
            className={v ? 'bar' : 'bar zero'}
          >
            <title>{`${labels[i]}: ${v}`}</title>
          </rect>
        ))}
      </svg>
      <div className="chartlabels">
        {labels.map((l, i) => (
          <span key={i} style={{ width: `${w}%` }}>
            {labels.length > 12 ? (i % 3 === 0 ? l : '') : l}
          </span>
        ))}
      </div>
    </div>
  )
}

function Tile({ big, label, sub }: { big: string; label: string; sub?: string }) {
  return (
    <div className="tile">
      <div className="tilebig">{big}</div>
      <div className="tilelabel">{label}</div>
      {sub && <div className="tilesub">{sub}</div>}
    </div>
  )
}

export function Reports() {
  const [board, setBoard] = useState<Dashboard | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [rows, setRows] = useState<ReportRows | null>(null)
  const [people, setPeople] = useState<MemberRows | null>(null)
  const [busy, setBusy] = useState(false)
  // The Calls report only. Its Calls | Messages switch (5 Oct 2026) became
  // the Reports / History bar (`ReportsHistory.tsx`, 6 Oct 2026).

  useEffect(() => {
    if (!can('reporting.dashboard')) return
    let alive = true
    const load = () =>
      api.dashboard().then((d) => { if (alive) setBoard(d) }).catch(() => {})
    load()
    // A dashboard is looked at, not watched. Slower than the queue.
    const t = setInterval(load, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  async function run() {
    setBusy(true)
    try {
      const body = { start: start || null, end: end || null }
      // THE CALLS TABLE IS THE EXPORT NODE'S AND BUILD REPORT IS NOT. The date
      // row is now drawn for either report, so a role holding only
      // `reporting.member_activity` presses Build report too — and this fetch
      // was unconditional, so its first act was a 403 from
      // `POST /api/reports/incidents`. That throws before the members fetch
      // below ever runs, so the one report the role IS allowed never arrives
      // and the screen reads as broken rather than as narrower.
      if (can('reporting.export')) {
        setRows(await api.reportIncidents(body))
      }
      if (can('reporting.member_activity')) {
        setPeople(await api.reportMembers(body))
      }
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not build the report.')
    } finally {
      setBusy(false)
    }
  }

  async function download(which: 'incidents' | 'members') {
    try {
      const body = { start: start || null, end: end || null, csv: true }
      const blob = which === 'incidents'
        ? await api.reportIncidentsCsv(body)
        : await api.reportMembersCsv(body)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${which}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not export.')
    }
  }

  return (
    <div className="workspace pad">
      {error && <div className="notice">{error}</div>}

      {can('reporting.dashboard') && board && (
        <>
          <h2>Right now</h2>
          <div className="tiles">
            <Tile big={String(board.open.total)} label="Open calls"
                  sub={`${board.open.unaccepted} nobody has taken`} />
            <Tile big={String(board.today.calls)} label="Calls today" />
            <Tile big={String(board.month.calls)} label="This month"
                  sub={`${board.last_month.calls} last month`} />
            <Tile
              big={minutes(board.month.median_minutes)}
              label="Median to on scene"
              // The denominator, always. Without it this is the middle of
              // however many happened to be timed.
              sub={board.month.timed
                ? `median of ${board.month.timed} timed call${board.month.timed === 1 ? '' : 's'}` +
                  (board.month.mean_minutes !== null
                    ? ` · mean ${minutes(board.month.mean_minutes)}` : '')
                : 'no call this month has an on-scene time yet'}
            />
            <Tile big={String(board.members.active_this_week)}
                  label="Members active this week"
                  sub={`${board.members.on_duty} on duty of ${board.members.on_roster}`} />
          </div>
          <p className="footnote">
            Times are {board.timezone}. Median rather than average on purpose —
            one call where somebody was stuck behind a train drags an average
            badly and makes a good month look poor. Calls logged after an
            outage are counted but never timed: their timestamps came from
            somebody's notes.
            {board.not_built.length > 0 && (
              <> Equipment figures are absent because equipment is not built
                 yet — a tile reading zero would be a fact about nothing.</>
            )}
          </p>
        </>
      )}

      {/* THE RANGE PICKER IS NOT THE EXPORT NODE'S, and drawing it inside that
          node cost a whole role its only report. Both date-range reports are
          built from one From/To pair and one button, so a role holding
          `reporting.member_activity` and not `reporting.export` opened this
          screen to a heading, no way to choose a range, and therefore no way to
          run the member table it had been ticked for — which from the far side
          of a missing control is indistinguishable from a feature nobody has
          built. Drawn for either report; the fetches in `run` and each download
          below names the exact key it needs. */}
      {(can('reporting.export') || can('reporting.member_activity')) && (
        <>
          <h2 className="rolesub">A date range</h2>
          <p className="blurb">
            A different job from the dashboard, and deliberately not crammed
            into it. Pick a range, read it, hand it to a board.
          </p>
          <div className="daterow">
            <label>From <input type="date" value={start}
                               onChange={(e) => setStart(e.target.value)} /></label>
            <label>To <input type="date" value={end}
                             onChange={(e) => setEnd(e.target.value)} /></label>
            <button className="primary compact" disabled={busy} onClick={run}>
              {busy ? 'Building…' : 'Build report'}
            </button>
            {/* NAMED, NOT INFERRED. `rows` is only ever set for a role that
                holds this key, so the implicit guard happened to be correct --
                and a gate that is correct by consequence stops being correct
                the first time somebody changes the consequence. Its sibling
                below names its keys; these two must not disagree about how a
                permission is stated. */}
            {rows && can('reporting.export') && (
              <button className="ghost" onClick={() => download('incidents')}>
                Export calls (CSV)
              </button>
            )}
            {/* BOTH KEYS, because the row around it is no longer the export
                block. `POST /api/reports/members` with `csv: true` asks for
                `reporting.export` as well — the reasoning is at
                app/routes/reports.py, where the rule belongs — so drawing this
                on `member_activity` alone would draw a button only to answer
                403. */}
            {people && can('reporting.export') && can('reporting.member_activity') && (
              <button className="ghost" onClick={() => download('members')}>
                Export members (CSV)
              </button>
            )}
          </div>
        </>
      )}

      {can('reporting.export') && (
        <>
          {rows?.chart && (
            <div className="charts">
              <Bars title="Calls by hour of day"
                    data={rows.chart.hours.map((h) => h.calls)}
                    labels={rows.chart.hours.map((h) => String(h.hour))} />
              <Bars title="Calls by day of week"
                    data={rows.chart.days.map((d) => d.calls)}
                    labels={DAYS} />
            </div>
          )}

          {rows?.by_nature && rows.by_nature.length > 0 && (
            <>
              <h3 className="rolesub">By nature</h3>
              {rows.by_nature.some((n) => n.with_others > 0) && (
                <p className="footnote">
                  A call with more than one nature is counted under each of them.
                </p>
              )}
              <div className="tablewrap">
                <table className="report">
                  <thead><tr>
                    <th>Nature</th><th>Calls</th><th>With another nature</th>
                  </tr></thead>
                  <tbody>
                    {rows.by_nature.map((n) => (
                      <tr key={n.nature}>
                        <td>{n.nature}</td>
                        <td className="mono">{n.calls}</td>
                        <td className="mono">{n.with_others || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {rows && (
            <>
              <h3 className="rolesub">
                Calls <span className="pill">{rows.rows.length}</span>
              </h3>
              {rows.rows.length === 0 ? (
                <div className="listempty">No calls in that range.</div>
              ) : (
                <div className="tablewrap">
                  <table className="report">
                    <thead><tr>
                      <th>Call</th><th>Received</th><th>Nature</th>
                      <th>Address</th><th>Status</th><th>On scene after</th>
                    </tr></thead>
                    <tbody>
                      {rows.rows.map((r) => (
                        <tr key={r.number}>
                          <td className="mono">{r.number}</td>
                          {/* The day alone when the time is not known (083). */}
                          <td>{r.called_at_unknown
                            ? `${new Date(r.called_at).toLocaleDateString()} · time not known`
                            : new Date(r.called_at).toLocaleString()}</td>
                          <td>{r.nature_label}</td>
                          <td>{r.place_label ?? '—'}</td>
                          <td>{r.status}</td>
                          <td>
                            {minutes(r.response_minutes)}
                            {/* Counted, never timed (083). */}
                            {r.times_not_known && <span className="tag">times not known</span>}
                            {r.logged_after_outage && (
                              <span className="tag">logged later</span>
                            )}
                            {/* Timed like any call (078); the tags say where
                                the figure came from. */}
                            {r.logged_afterwards && <span className="tag">from paper</span>}
                            {r.corrected && <span className="tag">corrected</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {people && can('reporting.member_activity') && (
        <>
          <h3 className="rolesub">Member activity</h3>
          <p className="footnote">
            Counts and hours, never a score, and sorted by name — a single
            ranked number cannot tell forty quick lockouts from eight floods
            that ran three hours each, and a leaderboard pushes people to grab
            calls they are not best placed for. These figures are for
            coordinators and are never shown to members.
          </p>
          <div className="tablewrap">
            <table className="report">
              <thead><tr>
                <th>Member</th><th>Callsign</th><th>Calls</th>
                <th>Primary</th><th>Backup</th><th>Hours</th>
              </tr></thead>
              <tbody>
                {people.rows.map((m) => (
                  <tr key={m.display_name + (m.callsign ?? '')}>
                    <td>{m.display_name}{!m.active && <span className="tag">retired</span>}</td>
                    <td className="mono">{m.callsign ?? '—'}</td>
                    <td>{m.calls}</td>
                    <td>{m.as_primary}</td>
                    <td>{m.as_backup}</td>
                    <td>{m.hours ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
